// Angular Files
import {
 Directive, ElementRef, HostListener, Input, AfterViewInit, OnChanges, OnDestroy, SimpleChanges,
 Inject, DOCUMENT
} from '@angular/core';

@Directive({
    selector: '[appRestrictFocus]',
    standalone: false
})
export class RestrictFocusDirective implements AfterViewInit, OnChanges, OnDestroy {
    @Input('appRestrictFocus') restricted: boolean; // Whether or not the focus is currently restricted

    // Add all the child elements inside the element which should be focusable
    private focusableElements = 'button, [href], input:not([type="hidden"]), select, textarea, [tabindex]:not([tabindex="-1"])';

    private hostElement;

    private firstFocusableElement;

    private focusableContent: Node[];

    private lastFocusableElement;

    private elementFocusedBeforeRestricted: HTMLElement;

    private releaseTimer: ReturnType<typeof setTimeout>;

    // Every instance whose restriction is currently on. Two overlap on every drawer-to-drawer
    // transition by design: the shell binds (openedStart) on each drawer to a handler that
    // closes its siblings, so the opening drawer is already restricted while the closing one
    // still is. Without this each would see the other's content as escaped focus and pull it
    // back - trading focus between them, synchronously, until the browser gives up with a
    // call-stack overflow.
    private static readonly restrictedInstances = new Set<RestrictFocusDirective>();

    constructor(
        @Inject(DOCUMENT) private document: Document,
        private el: ElementRef
    ) {}

    ngOnChanges(changes: SimpleChanges) {
        const change = changes.restricted;

        if (!change || change.currentValue === change.previousValue) return;

        if (this.restricted) {
            // This host's own release re-check can still be pending when it re-opens inside the
            // same tick. Left to fire it would see focus on the body and push it out to the
            // main-content anchor, back outside the restriction that just re-engaged.
            clearTimeout(this.releaseTimer);

            // Remember where focus came from so it can be handed back when the restriction
            // lifts. For a host opened from the page this is the control the user operated: an
            // open host's scrim swallows the clicks that would reach a second host's control,
            // and the Tab handling below keeps the keyboard inside, so nothing but that control
            // can have focus. A host opened over another one - the cart opening programmatically
            // on itemAdded$ or openCartRequested$ - instead captures whatever the closing host
            // had dragged into itself, and hands focus back to a detached node. The registry
            // below stops the two fighting; it does not make that capture right.
            this.elementFocusedBeforeRestricted = this.document.activeElement as HTMLElement;

            RestrictFocusDirective.restrictedInstances.add(this);

            return;
        }

        RestrictFocusDirective.restrictedInstances.delete(this);

        // The first binding arrives as undefined -> false, which is not a restriction lifting.
        // Nothing was ever captured to hand back, so there is nothing to release.
        if (change.firstChange) return;

        this.releaseFocus();
    }

    ngAfterViewInit() {
        this.grabFocusableElements();
        this.focus();
    }

    ngOnDestroy() {
        // A host torn down while still restricted would otherwise sit in the registry forever
        RestrictFocusDirective.restrictedInstances.delete(this);

        // Cancel any pending release re-check on teardown
        clearTimeout(this.releaseTimer);
    }

    // Catch programmatic focus changes that escape the restricted area
    @HostListener('document:focusin', ['$event']) onFocusIn(e: FocusEvent) {
        if (!this.restricted) return;

        const target = e.target as HTMLElement;

        // Allow focus on transient overlays (load panel, CDK overlays)
        if (target.closest('teller-online-load-panel')
            || target.closest('.cdk-overlay-container')) {
            return;
        }

        // Another restricted host legitimately holds focus - it opened while this one is still
        // closing. Whoever focus reached keeps it; pulling it back here starts the fight.
        if (RestrictFocusDirective.isInsideARestrictedHost(target, this)) {
            return;
        }

        // If focus escaped the restricted area, redirect it back
        if (!this.hostElement?.contains(target)) {
            this.grabFocusableElements();
            this.firstFocusableElement?.focus();
        }
    }

    // We can't filter for just the tab key in the listener as we won't get in here for shift so listen to the keydown in general and filter out later
    @HostListener('document:keydown', ['$event']) onKeyDown(e) {
        const isTabPressed = e.key === 'Tab' || e.keyCode === 9;

        // If it's not the tab key, we don't care about the event
        if (!isTabPressed) {
            return;
        }

        if (!this.restricted) return;

        // Don't interfere with focus inside overlays (dialogs, etc.)
        if ((this.document.activeElement as HTMLElement)
            ?.closest('.cdk-overlay-container')) {
            return;
        }

        // Always regrab the elements incase the contents has changed since it was first initialized
        this.grabFocusableElements();

        const active = this.document.activeElement;

        if (!this.hostElement.contains(active)) {
            // A drawer whose content is gated on a loading flag can be open and restricted
            // with nothing focusable inside it yet, so there may be no first element
            this.firstFocusableElement?.focus();

            return;
        }

        // Inside the host but not one of its listed controls: Chrome makes a scrollable
        // container keyboard-focusable, so Tab can land on the help drawer's article
        // scroller. Snapping to the first control from there would skip everything after
        // the scroller, so let the browser's own order carry on from it.
        if (!this.focusableContent.includes(active)) return;

        // Wrap the ends of the ring; every control between them is the browser's to handle
        if (e.shiftKey && active === this.firstFocusableElement) {
            this.lastFocusableElement.focus();
            e.preventDefault();
        } else if (!e.shiftKey && active === this.lastFocusableElement) {
            this.firstFocusableElement.focus();
            e.preventDefault();
        }
    }

    private focus() {
        if (this.restricted) {
            // A host can be rendered already open with its content still gated on a loading
            // flag, leaving nothing focusable inside it yet
            this.firstFocusableElement?.focus();
        }
    }

    /**
     * Hands focus back to whatever held it before the restriction started.
     *
     * A host that closes itself - a Material drawer, for instance - moves focus back to the
     * control that opened it synchronously, from inside the same event handler that flips this
     * directive's input to false. Angular only writes the new input value on the change
     * detection pass that follows, so at the moment of that hand-back onFocusIn still reads
     * `restricted` as true and drags focus straight back into the host. The host is then
     * hidden, the browser drops focus to the body, and the user is left with no visible focus
     * ring and a tab order that resumes from wherever the hidden host sits in the document.
     * Redoing the hand-back here, once the input has actually been written, is what makes it
     * stick.
     */
    private releaseFocus() {
        clearTimeout(this.releaseTimer);

        const target = this.elementFocusedBeforeRestricted;

        this.elementFocusedBeforeRestricted = null;

        const active = this.document.activeElement;

        // The body is never a deliberate focus target - it is where the browser drops focus
        // when whatever held it was hidden or removed, which is the state being repaired here
        const focusIsLost = !active || active === this.document.body;

        // Only reclaim focus this directive is actually holding - if focus already moved on to
        // a real control, pulling it back would be the same kind of theft this is meant to undo
        if (focusIsLost || this.el.nativeElement.contains(active)) {
            // The control that opened the host can be gone by the time the restriction lifts -
            // it can even be removed by the very change that lifted it, as when widening the
            // viewport past the wide breakpoint closes the mobile menu and, in the same pass,
            // drops the hamburger that opened it. Leaving focus on the body restarts the tab
            // order at the top of the document, so fall back the way
            // TellerOnlineFocusRestorationService does for the drawers that save focus by tag.
            const opener = target?.isConnected ? target : this.mainContent();

            opener?.focus();
        }

        // Whatever holds focus now can be removed by the change detection pass still running:
        // the opener handed focus above while it was still connected, or an outside control
        // focus never left - a host with nothing focusable can't pull focus in, and neither can
        // a drawer that closes without an animation, as it is already display: none when
        // onFocusIn tries to drag its own hand-back inside. Only the settled DOM shows that
        // focus was dropped, so re-check once the pass - and the host's own removal - is done.
        // The re-check only moves focus off the body, so it can never take it from a control.
        this.releaseTimer = setTimeout(() => {
            if (this.document.activeElement === this.document.body) {
                this.mainContent()?.focus();
            }
        });
    }

    /** Whether `target` sits inside a restricted host other than `ignore`. */
    private static isInsideARestrictedHost(target: HTMLElement, ignore: RestrictFocusDirective) {
        if (!target) return false;

        for (const instance of RestrictFocusDirective.restrictedInstances) {
            if (instance !== ignore && instance.el.nativeElement.contains(target)) return true;
        }

        return false;
    }

    /**
     * The page's focus anchor.
     *
     * `#main-content-focus` is rendered by routed page templates, never by either shell, and
     * plenty of routes carry none - four of the profile pages among them. On those the hand-back
     * would otherwise have nowhere to go, so fall back to the shell's own `<main>`, which both
     * portals render and which carries `tabindex="-1"` for exactly this.
     */
    private mainContent() {
        return this.document.querySelector<HTMLElement>('#main-content-focus')
            ?? this.document.querySelector<HTMLElement>('main#maincontent');
    }

    private grabFocusableElements() {
        this.hostElement = this.el.nativeElement;
        // Grab all the elements that can be focused within the parent element
        this.focusableContent = Array.from(this.hostElement.querySelectorAll(this.focusableElements));
        // Extract out just the first focusable element
        [this.firstFocusableElement] = this.focusableContent;
        // Extract out just the last focusable element within the parent
        this.lastFocusableElement = this.focusableContent[this.focusableContent.length - 1];
    }
}
